import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright   : 2018 semantics GmbH
//  Date        : $Date: 2026-10-07 15:01:10 +0200 (Wed, 07 Oct 2026) $
//  Author      : Dirk Rothe
//  Worker      : $Author: o.juenger $
//  Revision    : $Rev: 166332 $
//  Purpose     : templating sagas
// =============================================================================
// ---Third-party imports
import React from 'react';
import now from 'performance-now';
import { Label, Icon, Button } from 'semantic-ui-react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
// ---Framework imports
import { map, filterItems, dict, size, callIfFunc } from 'ldash';
import connect from 'uix/connect';
import { decodeGo } from 'uix/templating';
import { Actions, Sel } from 'uix/registries';
import log from 'uix/logging';
import styles from 'uix/styles';
import { ensurePathPrefix } from 'uix/router';
import { useState, useEffectSSR as useEffect, useApi, useSelector } from 'uix/hooks';
import { VIcon } from 'uix/graphics';
// ---Globals
const LEGACY_CLASSES = [
    'nav'
];
const decodeGoLazy = (isLazyPending, request, oldRequest, replacedQueryParams, domains, templatingcfgs, deploy, routingBeautify)=>{
    if (isLazyPending) {
        return {
            pathname: ''
        };
    } else {
        oldRequest = {
            ...oldRequest,
            query: {
                ...oldRequest.query,
                ...replacedQueryParams
            }
        };
        return decodeGo(request, oldRequest, domains, templatingcfgs, deploy, routingBeautify);
    }
};
const storeMap = (root)=>({
        oldRequest: root.meta.request,
        replacedQueryParams: root.meta.replacedQueryParams,
        templatingcfgs: root.config.templating,
        domains: root.config.domains,
        routingBeautify: root.config.routingBeautify,
        loading: root.config.loading,
        deploy: root.config.deploy,
        api_offline: root.meta.state == 'API_OFFLINE',
        app_running: root.meta.state == 'APP_RUNNING',
        apiurl: Sel.apiurl(root)
    });
const actionMap = {
    go: Actions.go,
    initDomain: Actions.initDomain
};
const mergeProps = (request, props)=>{
    let { dname, odid, classname, tabname, ctxid, id, query, updateQuery } = props;
    let mergeRequest = dict(filterItems({
        dname,
        odid,
        classname,
        tabname,
        ctxid,
        id,
        query,
        updateQuery
    }, (k, v)=>v !== undefined));
    return {
        ...request,
        ...mergeRequest
    };
};
const isDisabled = ({ disabled, api_offline, app_running })=>disabled || api_offline || !app_running;
const Link = ({ // direct path fed into go action
path = '', // go action compatible request (dname, classname, tabname, id, ...)
request = {}, // react children elements - probably a text node
children = null, title = null, // css related
cut = false, // behavior
disabled = false, lazy = false, external = false, // external link target if "path" is given
// overwrite with null for open in current tab
target = '_new', ...props })=>{
    /* merge templating params into a request prop rendered as <a>
  dname, odid, classname, tabname, ctxid, id, query, updateQuery
  are allowed values here (via mergeProps)
  */ const [hovered, setHovered] = useState(false);
    const { oldRequest, replacedQueryParams, domains, templatingcfgs, routingBeautify, loading, deploy, api_offline, app_running, go, initDomain, style, className, // custom click handling, request => customizedRequest (abort go() if customizedRequest === null)
    onClick, // simple click callback with event param
    onClicked } = props;
    const isLazyPending = ()=>lazy && !hovered;
    // decode link target and lazy-init the target domain on demand
    // (was getDerivedStateFromProps in the class based form)
    // BEWARE: must run during render in SSR (useEffectSSR), plain useEffect never
    // runs under renderToString, so INIT_DOMAIN for lazy links would be lost
    // (see watchInitDomainSSR in sagas/index.js)
    useEffect(()=>{
        if (!domains || path || isDisabled({
            disabled,
            api_offline,
            app_running
        })) {
            return;
        }
        try {
            decodeGoLazy(lazy && !hovered, mergeProps(request, props), oldRequest, replacedQueryParams, domains, templatingcfgs, deploy, routingBeautify);
        } catch (e) {
            if (e.code === 'E_DOMAIN_UNLOADED') {
                let isLoading = loading[e.dname];
                let ddata = domains.data[e.dname];
                if (!ddata) {
                    log.warn('bad dname %o', e.dname);
                } else if (!isLoading && !ddata.isGhost) {
                    initDomain(e.dname, 'link');
                }
            } else {
                log.warn('cannot decode %o', request);
            //~ throw e
            }
        }
    });
    const isTabAvailable = ({ namedParams, templatingcfg })=>{
        if (isLazyPending()) return true;
        let { classname, tabname } = namedParams;
        if (LEGACY_CLASSES.includes(classname)) {
            return true;
        }
        return !!templatingcfg.classes[classname]?.tabs[tabname];
    };
    const mergedRequest = mergeProps(request, props);
    let { rel, 'aria-label': ariaLabel } = props;
    let linkProps = {
        className: clsx(className, cut && styles.cut),
        style,
        title,
        ...rel ? {
            rel
        } : {},
        ...ariaLabel ? {
            'aria-label': ariaLabel
        } : {},
        onMouseEnter: ()=>!hovered && setHovered(true)
    };
    // if api is offline switch to <span> building instead of <a> building
    let disabledLink = isDisabled({
        disabled,
        api_offline,
        app_running
    });
    if (!path) {
        let onLinkClick = (e)=>{
            // e.stopPropagation();
            e.preventDefault();
            callIfFunc(onClicked, e);
            let customizedRequest = onClick ? onClick(mergedRequest) : mergedRequest;
            if (customizedRequest === undefined) {
                // onClick without result
                // BEWARE: connection a redux action will not trigger this case as it returns a action struct
                // we could detect this via checking {type, payload, meta} here
                go(mergedRequest);
            } else if (customizedRequest) {
                go(customizedRequest);
            }
        };
        let p = '';
        let decoded;
        try {
            decoded = decodeGoLazy(isLazyPending(), mergedRequest, oldRequest, replacedQueryParams, domains, templatingcfgs);
            p = decoded.pathname;
        } catch (e) {
            if (e.code === 'E_DOMAIN_UNLOADED') {
                return /*#__PURE__*/ _jsx("span", {
                    className: cut ? styles.cut : null,
                    style: {
                        ...style,
                        cursor: 'wait'
                    },
                    children: children
                });
            } else {
                return /*#__PURE__*/ _jsx("span", {
                    className: cut ? styles.cut : null,
                    style: {
                        color: 'red'
                    },
                    children: children
                });
            }
        }
        // if target class/tab is not available in templatingcfg, switch to disabled (or mark?)
        if (!isTabAvailable(decoded)) {
            log.warn('disable link, view not available: ', decoded.pathname);
            disabledLink = true;
        }
        // build full url for href indicator
        if (mergedRequest.keepQuery && oldRequest) {
            const str = map(oldRequest, (v, k)=>`${k}=${encodeURIComponent(v)}`).join('&');
            if (str) {
                p += `?${str}`;
            }
        } else if (size(decoded.query) > 0) {
            let str = map(decoded.query, (v, k)=>`${k}=${encodeURIComponent(v)}`).join('&');
            p += `?${str}`;
        }
        p = ensurePathPrefix(deploy.pathPrefix, p);
        if (!external) {
            linkProps = {
                ...linkProps,
                href: p,
                onClick: onLinkClick
            };
        } else {
            // external link
            linkProps = {
                ...linkProps,
                href: p,
                target
            };
        }
    } else {
        // path given: external link
        linkProps = {
            ...linkProps,
            href: path,
            target
        };
    }
    let { icon, basic, size: suiSize, floated, as: elementType, secondary } = props;
    if ([
        'button',
        'label'
    ].includes(elementType) || icon || basic) {
        let content = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                icon && /*#__PURE__*/ _jsx(Icon, {
                    name: icon
                }),
                children
            ]
        });
        let E = {
            button: Button,
            label: Label
        }[elementType] || Button;
        return /*#__PURE__*/ React.createElement(E, {
            ...linkProps,
            floated,
            basic,
            secondary,
            compact: true,
            size: suiSize,
            children: content
        });
    } else {
        if (disabledLink) {
            let { href, onClick, ...remaining } = linkProps;
            linkProps = {
                ...remaining,
                className: clsx(linkProps.className, styles.disabledLink)
            };
        }
        return /*#__PURE__*/ _jsx("a", {
            ...linkProps,
            children: children
        });
    }
};
Link.propTypes = {
    domains: PropTypes.object.isRequired
};
const ConnectedLink = connect('Link', {
    storeMap,
    actionMap
}, Link);
export { ConnectedLink as Link };
export const UnknownDomainLink = ({ children, ...props })=>{
    /* use these links only sparsly
  we should know the domain of a otid to keep the number of resolving calls low
  */ let [active, setActive] = useState(false);
    let { data, error } = useApi('getDomainId', {
        otid: props.id
    }, {
        condition: active
    });
    if (!data) {
        return /*#__PURE__*/ _jsx("a", {
            href: "#",
            onMouseEnter: ()=>setActive(true),
            children: children
        });
    }
    return /*#__PURE__*/ _jsx(ConnectedLink, {
        odid: data.odid,
        ...props,
        children: children
    });
};
export const ApiLink = ({ children, path, ...props })=>/*#__PURE__*/ _jsx(ConnectedLink, {
        path: useSelector(Sel.apiurl).href(path),
        ...props,
        children: children
    });
export const RawLink = ({ children, linkIcon, target, ...props })=>{
    let user = useSelector(Sel.user);
    if (user.client == 'kiosk') {
        // no new tabs in kiosk mode
        target = undefined;
    }
    return /*#__PURE__*/ _jsxs("a", {
        target: target,
        ...props,
        children: [
            children,
            ' ',
            linkIcon && /*#__PURE__*/ _jsx(VIcon, {
                style: {
                    fontSize: '.75em'
                },
                name: typeof linkIcon === 'string' ? linkIcon : 'link'
            })
        ]
    });
};
